import React from "react";

import classNames from "classnames";
import ReactDOMServer from "react-dom/server";
import { useSelector } from "react-redux";
import { NavLink } from "react-router-dom";

import { APP_TOOLTIP_ID } from "@/src/constants/tooltip";

import LogoutButton from "../menu/LogountButton";
import MenuLangSelector from "../menu/MenuLangSelector";

import type { JSX } from "react";

import type { AppState } from "../../reducers";

interface AdminAvatarProps {
  withDetails?: boolean;
  withLogout?: boolean;
  isCollapsed?: boolean;
  collapsedSidebarClassNames: (isCollapsed?: boolean) => string;
}

export default function AdminAvatar({
  withDetails,
  withLogout,
  isCollapsed,
  collapsedSidebarClassNames,
}: AdminAvatarProps): JSX.Element {
  const user = useSelector((state: AppState) => state.fetchReducer.user?.data);
  const orgaConfig = useSelector(
    (state: AppState) => state.fetchReducer.orga_config.data,
  );

  const profileClassNames = classNames(
    "mx-[0.78125rem] flex gap-[0.78125rem] text-muted-foreground transition-all duration-100 ease-in hover:text-muted-foreground active:text-muted-foreground rounded-[0.46875rem] px-[0.625rem] py-[0.3125rem]",
    !isCollapsed && "hover:bg-surface-muted",
  );

  const avatarClassNames = classNames(
    "shrink-0 rounded-full bg-cover bg-center transition-all duration-300 ease-in-out",
    isCollapsed
      ? "hover:opacity-80 size-[2.5rem] -translate-x-[1rem]"
      : "size-[55px]",
  );

  const detailsClassNames = classNames(
    "flex flex-col justify-center overflow-hidden [&>div]:truncate",
    collapsedSidebarClassNames(isCollapsed),
  );

  const userDetails = (
    <div className="flex flex-col gap-0 text-[0.703125rem]">
      <div>{`${user.first_name} ${user.last_name}`}</div>
      <div>{user.email}</div>
      <div>{orgaConfig?.organization_label}</div>
    </div>
  );

  return (
    <div>
      <NavLink to="/profile">
        <div
          className={profileClassNames}
          data-tooltip-hidden={!isCollapsed}
          data-tooltip-html={ReactDOMServer.renderToStaticMarkup(userDetails)}
          data-tooltip-id={APP_TOOLTIP_ID}
          data-tooltip-place="right"
          title={`${user.first_name} ${user.last_name} ${user.email}`}
        >
          <div
            className={avatarClassNames}
            style={{
              backgroundImage: `url(${user.avatar_url})`,
            }}
          />
          {withDetails ? (
            <div className={detailsClassNames}>{userDetails}</div>
          ) : null}
        </div>
      </NavLink>
      <MenuLangSelector
        collapsedSidebarClassNames={collapsedSidebarClassNames}
        isCollapsed={isCollapsed}
      />
      {withLogout ? (
        <LogoutButton
          collapsedSidebarClassNames={collapsedSidebarClassNames}
          isCollapsed={isCollapsed}
        />
      ) : null}
    </div>
  );
}
